import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { FALLBACK_ITEMS, sortItems } from './data/items';
import { FALLBACK_CATEGORIES, categoryName, sortCategories } from './data/categories';
import { FALLBACK_TABLES, PARCEL_TABLE, sortTables, tableLabel } from './data/tables';
import { FALLBACK_SETTINGS } from './data/settings';
import {
  FALLBACK_EXPENSE_CATEGORIES, sortExpenseCategories,
} from './data/expenseCategories';
import { sortExpenses } from './logic/expenses';
import { newId } from './ids';
import { translate } from './i18n';
import { cartTotals, clampPct, effectivePrice } from './logic/pricing';
import { isProbablyOnline } from './net';
import { deviceStorage } from './storage';
import { getSupabase } from './supabase';
import { isCloudConfigured } from './config';
import {
  fetchMembership, pullSnapshot, pushMutation, setActiveRestaurant,
} from './sync/engine';
import { dequeue, enqueue, markFailed, sorted } from './sync/outbox';
import type { Mutation, OutboxEntry, SyncStatus } from './sync/types';
import type {
  AppSettings, CartLine, Category, Customer, Expense, ExpenseCategory, Lang,
  MenuItem, OpenTable, Order, Restaurant, RestaurantTable, StaffMember,
  StaffRole, TextSize, ThemeMode,
} from './types';

export type ViewId =
  | 'dashboard' | 'menu' | 'order' | 'orders' | 'customers' | 'offers'
  | 'analytics' | 'expenses' | 'more' | 'admin' | 'staff';

/** Where the New Order flow currently is: table -> category -> items -> review. */
export type OrderStep = 'tables' | 'categories' | 'items' | 'cart';

const EMPTY_CUSTOMER: Customer = { name: '', phone: '' };

function newTable(tableId: string): OpenTable {
  const now = Date.now();
  return {
    tableId,
    cart: [],
    discount: 0,
    customer: { ...EMPTY_CUSTOMER },
    openedAt: now,
    updatedAt: now,
  };
}

interface AppState {
  lang: Lang;
  /** Light is the default; the toggle lives next to the language picker. */
  theme: ThemeMode;
  /** Reading size. Defaults to 'md', which is larger than the original design. */
  textSize: TextSize;
  view: ViewId;
  search: string;
  /** Category filter on the Menu tab (not the order flow). */
  activeCat: string;

  /** The live menu. Seeded from the bundle, replaced by the database. */
  categories: Category[];
  menuItems: MenuItem[];
  /** The live floor plan. Same story as the menu: bundle first, database wins. */
  restaurantTables: RestaurantTable[];
  /** Brand, contact details and discount rules. Bundle first, database wins. */
  settings: AppSettings;

  /** itemId -> discount percent. Absent means no offer. */
  offers: Record<string, number>;

  /**
   * Money out. Owner-only, and the database is what enforces that — see the
   * RLS notes in supabase/migrations/20260928120000_expenses.sql.
   *
   * Emptied the moment this device is not signed in as an owner of the active
   * restaurant. These tills sit on a counter and are shared: leaving the
   * owner's rent and payroll in device storage for whoever signs in next is
   * the one way this feature could leak, and hiding the tab would not stop it.
   */
  expenses: Expense[];
  /** The spending heads. Bundle first, database wins — same as the menu. */
  expenseCategories: ExpenseCategory[];

  /** Tables with an order in progress, keyed by table id. */
  tables: Record<string, OpenTable>;
  /**
   * Extra table ids added on the pre-floor-plan build. Kept so an older device's
   * tables still resolve after it syncs; new tables go to `restaurantTables`.
   */
  customTables: string[];

  activeTable: string | null;
  orderStep: OrderStep;
  /** Category being browsed inside the order flow. */
  flowCat: string | null;

  orders: Order[];
  /** Order whose full receipt is open, if any. */
  openOrderId: string | null;

  toast: { id: number; message: string } | null;

  // ---- sync ----
  /** Stable per-install id, so the till can be reconciled per device. */
  deviceId: string;
  /** Writes made locally that have not reached the server yet. */
  outbox: OutboxEntry[];
  sync: SyncStatus;
  /** Signed-in user's email, or null. */
  userEmail: string | null;

  // ---- who, and where ----
  /** Restaurants this user belongs to. Empty means no access. */
  restaurants: Restaurant[];
  /** The restaurant currently being worked in. */
  restaurantId: string | null;
  /** This user's role in the active restaurant. Null when signed out. */
  role: StaffRole | null;
  /**
   * Runs the software rather than a restaurant: creates shops and appoints
   * their owners, is on no roster and takes no orders. Separate from `role`
   * because `role` says what somebody is *within* one restaurant, and an admin
   * is deliberately within none.
   */
  isPlatformAdmin: boolean;
  /** The roster of the active restaurant. Only owners can change it. */
  staff: StaffMember[];
  /** True once persisted state has been read back from the device. */
  hydrated: boolean;

  setLang: (lang: Lang) => void;
  setTheme: (theme: ThemeMode) => void;
  setTextSize: (size: TextSize) => void;
  toggleTheme: () => void;
  setView: (view: ViewId) => void;
  setSearch: (search: string) => void;
  setActiveCat: (cat: string) => void;

  upsertCategory: (cat: Category) => void;
  deleteCategory: (id: string) => void;
  upsertMenuItem: (item: MenuItem) => void;
  deleteMenuItem: (id: string) => void;

  upsertRestaurantTable: (table: RestaurantTable) => void;
  deleteRestaurantTable: (id: string) => void;
  updateSettings: (patch: Partial<AppSettings>) => void;

  /** Marks an order done. Owners only — the database refuses a worker. */
  /** "Continue": pushes the next reminder out by half an hour. */

  selectRestaurant: (id: string) => void;
  upsertRestaurant: (restaurant: Restaurant) => void;
  /** Gives a brand-new restaurant a starter menu, floor plan and settings. */
  seedRestaurant: (restaurant: Restaurant) => void;
  upsertStaff: (member: StaffMember) => void;
  /** Drops the row from the local roster; see the implementation. */
  removeStaff: (id: string) => void;

  /** Records one payment out. Mints the id, so the caller passes the facts. */
  addExpense: (input: {
    categoryId: string | null;
    amount: number;
    payee: string;
    note: string;
    paidAt: number;
  }) => void;
  /** Amends an entry already recorded. */
  updateExpense: (expense: Expense) => void;
  deleteExpense: (id: string) => void;
  upsertExpenseCategory: (category: ExpenseCategory) => void;
  /** Retires a head. Entries booked against it survive as uncategorised. */
  deleteExpenseCategory: (id: string) => void;

  setItemOffer: (itemId: string, pct: number) => void;
  setCategoryOffer: (catId: string, pct: number) => void;
  clearAllOffers: () => void;

  openTable: (tableId: string) => void;
  addCustomTable: (label: string) => void;
  selectFlowCat: (catId: string) => void;
  goToStep: (step: OrderStep) => void;
  goBack: () => void;

  addToCart: (itemId: string, variant: string) => void;
  changeQty: (index: number, delta: number) => void;
  setOrderDiscount: (pct: number) => void;
  setCustomer: (patch: Partial<Customer>) => void;
  clearTable: (tableId?: string) => void;
  placeOrder: () => void;

  openOrderDetail: (id: string) => void;
  closeOrderDetail: () => void;

  showToast: (message: string) => void;
  dismissToast: () => void;

  // ---- sync actions ----
  queue: (mutation: Mutation) => void;
  /** Queues the current state of the open table as one collapsible write. */
  queueActiveTable: () => void;
  markHydrated: () => void;
  loadMembership: () => Promise<void>;
  syncNow: (opts?: { pull?: boolean }) => Promise<void>;
  signIn: (email: string, password: string) => Promise<string | null>;
  /**
   * Replaces the owner-issued password with one this person chose.
   * Returns an error message, or null on success.
   */
  /**
   * Replaces this person's password with one they choose.
   *
   * `current` is not ceremony: on a shared counter tablet the session is
   * whoever last signed in, and it is often left signed in all day. Without
   * proving the old password, anyone passing the till could lock the actual
   * owner out of their own account in four taps.
   *
   * Returns an error message, or null on success.
   */
  changePassword: (current: string, next: string) => Promise<string | null>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

let toastSeq = 0;

/** The name of a restaurant in the cached list, or a neutral placeholder. */
function restaurantName(s: { restaurants: Restaurant[] }, id: string): string {
  return s.restaurants.find((r) => r.id === id)?.name ?? FALLBACK_SETTINGS.brandName;
}

const initialSync: SyncStatus = {
  state: 'offline',
  pending: 0,
  lastSyncedAt: null,
  lastError: null,
};

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      lang: 'en',
      theme: 'light',
      textSize: 'md',
      view: 'dashboard',
      search: '',
      activeCat: 'all',
      categories: sortCategories(FALLBACK_CATEGORIES),
      menuItems: sortItems(FALLBACK_ITEMS),
      restaurantTables: sortTables(FALLBACK_TABLES),
      settings: { ...FALLBACK_SETTINGS },
      offers: {},
      expenses: [],
      expenseCategories: sortExpenseCategories(FALLBACK_EXPENSE_CATEGORIES),
      tables: {},
      customTables: [],
      activeTable: null,
      orderStep: 'tables',
      flowCat: null,
      orders: [],
      openOrderId: null,
      toast: null,

      deviceId: newId(),
      outbox: [],
      sync: initialSync,
      userEmail: null,
      restaurants: [],
      restaurantId: null,
      role: null,
      isPlatformAdmin: false,
      staff: [],
      hydrated: false,

      setLang: (lang) => set({ lang }),
      setTheme: (theme) => set({ theme }),
      setTextSize: (textSize) => set({ textSize }),
      toggleTheme: () => set((s) => ({ theme: s.theme === 'light' ? 'dark' : 'light' })),

      // Switching tabs clears the search box. Opening New Order also drops back
      // to the table picker: the tab is called "New Order", and resuming
      // whichever table was last touched meant an item could land on somebody
      // else's bill. To add to a table already running, tap it — on the picker,
      // or on the Dashboard under Running tables.
      setView: (view) =>
        set(
          view === 'order'
            ? { view, search: '', orderStep: 'tables' as OrderStep, activeTable: null, flowCat: null }
            : { view, search: '' },
        ),

      // Typing in search jumps to the Menu tab, wherever you were.
      setSearch: (search) =>
        set((s) => (s.view === 'menu' ? { search } : { search, view: 'menu' as ViewId })),

      setActiveCat: (activeCat) => set({ activeCat }),

      // ---- menu management ----

      upsertCategory: (cat) => {
        set((s) => ({
          categories: sortCategories([
            ...s.categories.filter((c) => c.id !== cat.id),
            cat,
          ]),
        }));
        get().queue({ kind: 'category', id: cat.id, category: cat });
      },

      deleteCategory: (id) => {
        // Items would be orphaned, so they go with it. Placed orders keep their
        // own copy of every line, so history is unaffected.
        set((s) => ({
          categories: s.categories.filter((c) => c.id !== id),
          menuItems: s.menuItems.filter((i) => i.cat !== id),
        }));
        const doomed = get().menuItems.filter((i) => i.cat === id);
        doomed.forEach((i) => get().queue({ kind: 'menu_item_delete', id: i.id }));
        get().queue({ kind: 'category_delete', id });
      },

      upsertMenuItem: (item) => {
        set((s) => ({
          menuItems: sortItems([...s.menuItems.filter((i) => i.id !== item.id), item]),
        }));
        get().queue({ kind: 'menu_item', id: item.id, item });
      },

      deleteMenuItem: (id) => {
        set((s) => ({ menuItems: s.menuItems.filter((i) => i.id !== id) }));
        get().queue({ kind: 'menu_item_delete', id });
      },

      // ---- floor plan ----

      upsertRestaurantTable: (table) => {
        set((s) => ({
          restaurantTables: sortTables([
            ...s.restaurantTables.filter((r) => r.id !== table.id),
            table,
          ]),
        }));
        get().queue({ kind: 'restaurant_table', id: table.id, table });
      },

      deleteRestaurantTable: (id) => {
        // An order in progress at that table would become unreachable, so it is
        // cleared first. Placed orders keep their own copy of the table id, so
        // history and receipts are unaffected.
        if (get().tables[id]) get().clearTable(id);
        set((s) => ({
          restaurantTables: s.restaurantTables.filter((r) => r.id !== id),
          customTables: s.customTables.filter((c) => c !== id),
        }));
        get().queue({ kind: 'restaurant_table_delete', id });
      },

      updateSettings: (patch) => {
        const next = { ...get().settings, ...patch };
        set({ settings: next });
        get().queue({ kind: 'settings', id: 'settings', settings: next });
      },

      // ---- restaurants and staff (owner only) ----

      selectRestaurant: (id) => {
        if (id === get().restaurantId) return;
        setActiveRestaurant(id);
        // EVERYTHING belonging to a restaurant is dropped, not just the orders.
        // The menu, floor plan and settings are per-restaurant too, and the
        // pull deliberately keeps the local copy when a table comes back empty
        // (that is what makes a fresh install work offline) — so leaving them
        // in place made a new shop show the previous shop's menu under its own
        // name. Resetting to the bundle means the worst case is the fallback
        // menu for a second, not somebody else's prices.
        set((s) => ({
          restaurantId: id,
          role: s.restaurants.some((r) => r.id === id) ? s.role : null,
          orders: [],
          tables: {},
          offers: {},
          staff: [],
          // Costs are per-restaurant too, and carrying one shop's payroll into
          // another shop's screen would be worse than showing it empty.
          expenses: [],
          expenseCategories: sortExpenseCategories(FALLBACK_EXPENSE_CATEGORIES),
          categories: sortCategories(FALLBACK_CATEGORIES),
          menuItems: sortItems(FALLBACK_ITEMS),
          restaurantTables: sortTables(FALLBACK_TABLES),
          settings: { ...FALLBACK_SETTINGS, brandName: restaurantName(s, id) },
          activeTable: null,
          orderStep: 'tables' as OrderStep,
          flowCat: null,
        }));
        void get().syncNow();
      },

      upsertRestaurant: (restaurant) => {
        const isNew = !get().restaurants.some((r) => r.id === restaurant.id);
        set((s) => ({
          restaurants: [
            ...s.restaurants.filter((r) => r.id !== restaurant.id),
            restaurant,
          ].sort((a, b) => a.name.localeCompare(b.name)),
        }));
        get().queue({ kind: 'restaurant', id: restaurant.id, restaurant });

        // A brand-new shop has no menu, no tables and no settings of its own.
        // Opening it and seeding it here means it is usable immediately rather
        // than showing an empty till the owner has to fill in by hand.
        if (isNew) {
          get().selectRestaurant(restaurant.id);
          get().seedRestaurant(restaurant);
        }
      },

      /**
       * Writes a starter menu, floor plan and settings for a restaurant that has
       * none. Queued like any other change, so it works offline and uploads
       * when the connection returns.
       */
      seedRestaurant: (restaurant) => {
        const prefix = restaurant.id.slice(0, 8);
        // Ids are the global primary key, so a second shop cannot reuse
        // 'special' or 'i1'. Prefixing keeps them unique without composite keys.
        const catId = (id: string) => `${prefix}-${id}`;

        const categories = sortCategories(
          FALLBACK_CATEGORIES.map((c) => ({ ...c, id: catId(c.id) })),
        );
        const menuItems = sortItems(
          FALLBACK_ITEMS.map((i) => ({ ...i, id: `${prefix}-${i.id}`, cat: catId(i.cat) })),
        );
        const tables = sortTables(FALLBACK_TABLES);
        // The heads are prefixed on the same rule as the menu ids, and the
        // prefix matches what the migration writes — so a shop seeded by the
        // app and one seeded by SQL end up with the same row ids.
        const expenseCategories = sortExpenseCategories(
          FALLBACK_EXPENSE_CATEGORIES.map((c) => ({ ...c, id: `${prefix}-exp-${c.id}` })),
        );
        const settings: AppSettings = {
          ...FALLBACK_SETTINGS,
          brandName: restaurant.name,
          address: restaurant.address,
          phone: restaurant.phone,
          gstin: restaurant.gstin,
        };

        set({
          categories, menuItems, restaurantTables: tables, settings,
          expenseCategories, expenses: [],
        });

        categories.forEach((c) => get().queue({ kind: 'category', id: c.id, category: c }));
        menuItems.forEach((i) => get().queue({ kind: 'menu_item', id: i.id, item: i }));
        tables.forEach((r) => get().queue({ kind: 'restaurant_table', id: r.id, table: r }));
        expenseCategories.forEach((c) =>
          get().queue({ kind: 'expense_category', id: c.id, category: c }),
        );
        get().queue({ kind: 'settings', id: 'settings', settings });
      },

      upsertStaff: (member) => {
        set((s) => ({
          staff: [...s.staff.filter((m) => m.id !== member.id), member].sort((a, b) =>
            a.email.localeCompare(b.email),
          ),
        }));
        get().queue({ kind: 'staff', id: member.id, member });
      },

      /**
       * Local only. Removing somebody has to also destroy their login, or a
       * dismissed worker keeps a working password, and only the server can do
       * that — so the caller sends `deleteStaffAccount(id)` first and this
       * merely catches the roster up.
       */
      removeStaff: (id) => {
        set((s) => ({ staff: s.staff.filter((m) => m.id !== id) }));
      },

      // ---- money out (owner only) ----

      addExpense: ({ categoryId, amount, payee, note, paidAt }) => {
        const expense: Expense = {
          // A real uuid: the column is uuid, and an id minted here is what
          // makes a retried upload update the row rather than duplicate it.
          id: newId(),
          categoryId,
          // Guarded here as well as at the field, because the column carries a
          // >= 0 check and a rejected write would sit in the outbox forever.
          amount: Math.max(0, Math.round(amount * 100) / 100),
          payee: payee.trim(),
          note: note.trim(),
          paidAt,
          synced: false,
        };
        set((s) => ({ expenses: sortExpenses([...s.expenses, expense]) }));
        get().queue({ kind: 'expense', id: expense.id, expense });
        get().showToast(
          translate(get().lang, 'expense_saved_toast', {
            amount: '₹' + Math.round(expense.amount),
          }),
        );
      },

      updateExpense: (expense) => {
        const next: Expense = {
          ...expense,
          amount: Math.max(0, Math.round(expense.amount * 100) / 100),
          payee: expense.payee.trim(),
          note: expense.note.trim(),
          synced: false,
        };
        set((s) => ({
          expenses: sortExpenses([
            ...s.expenses.filter((e) => e.id !== next.id),
            next,
          ]),
        }));
        get().queue({ kind: 'expense', id: next.id, expense: next });
        get().showToast(translate(get().lang, 'saved_toast'));
      },

      deleteExpense: (id) => {
        set((s) => ({ expenses: s.expenses.filter((e) => e.id !== id) }));
        get().queue({ kind: 'expense_delete', id });
        get().showToast(translate(get().lang, 'deleted_toast'));
      },

      upsertExpenseCategory: (category) => {
        set((s) => ({
          expenseCategories: sortExpenseCategories([
            ...s.expenseCategories.filter((c) => c.id !== category.id),
            category,
          ]),
        }));
        get().queue({ kind: 'expense_category', id: category.id, category });
      },

      deleteExpenseCategory: (id) => {
        // The entries stay. Deleting "Vegetables" must not erase two years of
        // vegetable spending — the money was paid either way, so the entries
        // drop their head and read as uncategorised, matching the ON DELETE
        // SET NULL on the column.
        set((s) => ({
          expenseCategories: s.expenseCategories.filter((c) => c.id !== id),
          expenses: s.expenses.map((e) =>
            e.categoryId === id ? { ...e, categoryId: null } : e,
          ),
        }));
        get().queue({ kind: 'expense_category_delete', id });
      },

      // ---- offers ----

      setItemOffer: (itemId, pct) => {
        const clamped = clampPct(pct, get().settings.maxDiscountPct);
        set((s) => ({ offers: { ...s.offers, [itemId]: clamped } }));
        get().queue({ kind: 'offer', id: itemId, itemId, pct: clamped });

        const { lang } = get();
        const name = get().menuItems.find((i) => i.id === itemId)?.name ?? '';
        get().showToast(
          clamped > 0
            ? translate(lang, 'offer_set_toast', { name, pct: clamped })
            : translate(lang, 'offer_cleared_toast', { name }),
        );
      },

      setCategoryOffer: (catId, pct) => {
        const clamped = clampPct(pct, get().settings.maxDiscountPct);
        const affected = get().menuItems.filter((i) => i.cat === catId);
        set((s) => {
          const offers = { ...s.offers };
          affected.forEach((i) => {
            offers[i.id] = clamped;
          });
          return { offers };
        });
        affected.forEach((i) =>
          get().queue({ kind: 'offer', id: i.id, itemId: i.id, pct: clamped }),
        );

        const { lang } = get();
        const name = categoryName(get().categories.find((c) => c.id === catId), lang);
        get().showToast(
          clamped > 0
            ? translate(lang, 'cat_offer_toast', { name, pct: clamped })
            : translate(lang, 'offer_cleared_toast', { name }),
        );
      },

      clearAllOffers: () => {
        const cleared = Object.keys(get().offers);
        set({ offers: {} });
        cleared.forEach((itemId) => get().queue({ kind: 'offer', id: itemId, itemId, pct: 0 }));
        get().showToast(translate(get().lang, 'all_offers_cleared_toast'));
      },

      // ---- order flow ----

      openTable: (tableId) =>
        set((s) => ({
          activeTable: tableId,
          orderStep: 'categories',
          flowCat: null,
          tables: s.tables[tableId] ? s.tables : { ...s.tables, [tableId]: newTable(tableId) },
        })),

      addCustomTable: (label) => {
        const id = label.trim();
        if (!id) return;
        const { restaurantTables } = get();
        if (!restaurantTables.some((r) => r.id === id)) {
          // Sits just before Parcel, which the seed puts at 1000.
          const dineIn = restaurantTables.filter((r) => r.kind === 'dine_in');
          const sortOrder = Math.max(0, ...dineIn.map((r) => r.sortOrder)) + 10;
          get().upsertRestaurantTable({
            id,
            // A typed name is a real name; a typed number renders as "Table 9".
            label: /^\d+$/.test(id) ? '' : id,
            labelHi: '',
            labelGu: '',
            seats: 0,
            kind: id === PARCEL_TABLE ? 'parcel' : 'dine_in',
            sortOrder: id === PARCEL_TABLE ? 1000 : sortOrder,
            active: true,
          });
        }
        get().openTable(id);
      },

      selectFlowCat: (catId) => set({ flowCat: catId, orderStep: 'items' }),
      goToStep: (orderStep) => set({ orderStep }),

      goBack: () =>
        set((s) => {
          if (s.orderStep === 'items') return { orderStep: 'categories', flowCat: null };
          if (s.orderStep === 'cart') return { orderStep: 'categories' };
          // From the category list, stepping back closes the table.
          return { orderStep: 'tables', activeTable: null, flowCat: null };
        }),

      addToCart: (itemId, variant) => {
        const item = get().menuItems.find((i) => i.id === itemId);
        const tableId = get().activeTable;
        if (!item || !tableId) return;

        const offerPct = get().offers[itemId] ?? 0;
        const basePrice = item.prices[variant];
        const price = effectivePrice(item, variant, offerPct);

        set((s) => {
          const table = s.tables[tableId] ?? newTable(tableId);
          const idx = table.cart.findIndex(
            (c) => c.itemId === itemId && c.variant === variant,
          );
          let cart: CartLine[];
          if (idx >= 0) {
            cart = table.cart.slice();
            cart[idx] = { ...cart[idx], qty: cart[idx].qty + 1 };
          } else {
            cart = [
              ...table.cart,
              {
                itemId,
                name: item.name,
                nameHi: item.nameHi,
                nameGu: item.nameGu,
                cat: item.cat,
                variant,
                basePrice,
                offerPct,
                price,
                qty: 1,
              },
            ];
          }
          return {
            tables: { ...s.tables, [tableId]: { ...table, cart, updatedAt: Date.now() } },
          };
        });

        get().queueActiveTable();
        const label = item.name + (variant !== 'Price' ? ` (${variant})` : '');
        get().showToast(translate(get().lang, 'added_toast', { name: label }));
      },

      changeQty: (index, delta) => {
        set((s) => {
          const tableId = s.activeTable;
          if (!tableId) return s;
          const table = s.tables[tableId];
          if (!table) return s;
          const line = table.cart[index];
          if (!line) return s;

          const qty = line.qty + delta;
          const cart =
            qty <= 0
              ? table.cart.filter((_, i) => i !== index)
              : table.cart.map((l, i) => (i === index ? { ...l, qty } : l));

          return {
            tables: { ...s.tables, [tableId]: { ...table, cart, updatedAt: Date.now() } },
          };
        });
        get().queueActiveTable();
      },

      setOrderDiscount: (pct) => {
        set((s) => {
          const tableId = s.activeTable;
          if (!tableId || !s.tables[tableId]) return s;
          return {
            tables: {
              ...s.tables,
              [tableId]: {
                ...s.tables[tableId],
                discount: clampPct(pct, s.settings.maxDiscountPct),
                updatedAt: Date.now(),
              },
            },
          };
        });
        get().queueActiveTable();
      },

      setCustomer: (patch) => {
        set((s) => {
          const tableId = s.activeTable;
          if (!tableId || !s.tables[tableId]) return s;
          const table = s.tables[tableId];
          return {
            tables: {
              ...s.tables,
              [tableId]: {
                ...table,
                customer: { ...table.customer, ...patch },
                updatedAt: Date.now(),
              },
            },
          };
        });
        get().queueActiveTable();
      },

      clearTable: (tableId) => {
        const id = tableId ?? get().activeTable;
        if (!id) return;
        const { lang } = get();
        set((s) => {
          const tables = { ...s.tables };
          delete tables[id];
          return {
            tables,
            ...(s.activeTable === id
              ? { activeTable: null, orderStep: 'tables' as OrderStep, flowCat: null }
              : null),
          };
        });
        get().queue({ kind: 'table_clear', id, tableId: id });
        const named = findTable(get().restaurantTables, id);
        get().showToast(
          translate(lang, 'table_cleared_toast', { name: tableLabel(lang, id, named) }),
        );
      },

      placeOrder: () => {
        const { activeTable, tables, orders, lang } = get();
        if (!activeTable) return;
        const table = tables[activeTable];
        if (!table || table.cart.length === 0) return;

        const { sub, disc, grand, itemSavings, qty } = cartTotals(table.cart, table.discount);

        const order: Order = {
          id: newId(),
          orderNo: null,
          localNo: orders.length + 1,
          tableId: activeTable,
          customer: { ...table.customer },
          items: table.cart.map((c) => ({ ...c })),
          qty,
          subtotal: sub,
          itemSavings,
          discountPct: table.discount,
          discountAmount: disc,
          total: grand,
          placedAt: Date.now(),
          synced: false,
        };

        const nextTables = { ...tables };
        delete nextTables[activeTable];

        set({
          orders: [...orders, order],
          tables: nextTables,
          activeTable: null,
          orderStep: 'tables',
          flowCat: null,
          // Open the receipt straight away: the next thing the counter does is
          // hand the customer their bill, and the Print button lives on it.
          openOrderId: order.id,
        });

        get().queue({ kind: 'order', id: order.id, order });
        get().queue({ kind: 'table_clear', id: activeTable, tableId: activeTable });

        get().showToast(
          translate(lang, 'order_placed_toast', {
            n: order.localNo,
            total: '₹' + Math.round(grand),
          }),
        );
      },

      openOrderDetail: (id) => set({ openOrderId: id }),
      closeOrderDetail: () => set({ openOrderId: null }),

      showToast: (message) => set({ toast: { id: ++toastSeq, message } }),
      dismissToast: () => set({ toast: null }),

      markHydrated: () => set({ hydrated: true }),

      // ---- sync ----

      queue: (mutation) => {
        set((s) => {
          const outbox = enqueue(s.outbox, mutation);
          return { outbox, sync: { ...s.sync, pending: outbox.length } };
        });
        // Fire and forget: the till must never wait on the network.
        void get().syncNow({ pull: false });
      },

      queueActiveTable: () => {
        const { activeTable, tables } = get();
        if (!activeTable) return;
        const table = tables[activeTable];
        if (!table) return;
        get().queue({
          kind: 'table',
          id: activeTable,
          tableId: activeTable,
          cart: table.cart,
          discount: table.discount,
          customer: table.customer,
          openedAt: table.openedAt,
        });
      },

      syncNow: async ({ pull = true } = {}) => {
        if (!isCloudConfigured()) {
          set((s) => ({ sync: { ...s.sync, state: 'offline', pending: s.outbox.length } }));
          return;
        }
        if (!get().userEmail) {
          set((s) => ({ sync: { ...s.sync, state: 'signed_out', pending: s.outbox.length } }));
          return;
        }
        // Membership decides which restaurant everything is scoped to, so it is
        // resolved before the first push. Without it a write has no
        // restaurant_id and the schema rejects it.
        //
        // Refreshed on EVERY sync, not only when no shop is selected yet. The
        // restaurant id is persisted, so the old `if (!restaurantId)` guard
        // meant that on any device which had ever synced, this never ran again:
        // a role changed in Admin never reached the tablet, somebody demoted to
        // worker kept the owner's tabs, and an administrator whose device
        // already had a shop selected never learned they were one. Three small
        // queries against a snapshot pull that is far larger.
        try {
          await get().loadMembership();
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          set((s) => ({
            sync: { ...s.sync, state: 'error', pending: s.outbox.length, lastError: msg },
          }));
          return;
        }
        // An admin is on no roster by design, so an empty restaurant list is
        // what success looks like for them, not a lockout.
        if (!get().restaurantId && !get().isPlatformAdmin) {
          set((s) => ({ sync: { ...s.sync, state: 'no_access', pending: s.outbox.length } }));
          return;
        }
        if (!isProbablyOnline()) {
          set((s) => ({ sync: { ...s.sync, state: 'offline', pending: s.outbox.length } }));
          return;
        }
        if (get().sync.state === 'syncing') return;

        set((s) => ({ sync: { ...s.sync, state: 'syncing', lastError: null } }));

        try {
          // 1. Push everything queued, oldest first.
          for (const entry of sorted(get().outbox)) {
            try {
              await pushMutation(entry.mutation, get().deviceId);
              set((s) => {
                const outbox = dequeue(s.outbox, entry.mutation);
                const orders =
                  entry.mutation.kind === 'order'
                    ? s.orders.map((o) =>
                        o.id === entry.mutation.id ? { ...o, synced: true } : o,
                      )
                    : s.orders;
                const expenses =
                  entry.mutation.kind === 'expense'
                    ? s.expenses.map((e) =>
                        e.id === entry.mutation.id ? { ...e, synced: true } : e,
                      )
                    : s.expenses;
                return {
                  outbox, orders, expenses,
                  sync: { ...s.sync, pending: outbox.length },
                };
              });
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              set((s) => ({ outbox: markFailed(s.outbox, entry.mutation, msg) }));
              throw err; // stop the flush; order matters
            }
          }

          // 2. Pull what the other devices did.
          if (pull) {
            const snap = await pullSnapshot();
            set((s) => {
              // Local orders still in the outbox have not been uploaded yet, so
              // keep them; everything else comes from the server.
              const unsynced = s.orders.filter((o) => !o.synced);
              const remoteIds = new Set(snap.orders.map((o) => o.id));
              const merged = [
                ...snap.orders,
                ...unsynced.filter((o) => !remoteIds.has(o.id)),
              ].sort((a, b) => a.placedAt - b.placedAt);

              // Money out is owner-only at the database, so a worker's pull
              // comes back empty because RLS refused it — not because nothing
              // was spent. Merging that would blank a real record. When this
              // user is not an owner the local copy is dropped outright, so a
              // shared counter tablet does not keep the owner's payroll in
              // device storage for whoever signs in next.
              const isOwner = s.role === 'owner';
              const unsentExpenses = s.expenses.filter((e) => !e.synced);
              const remoteExpenseIds = new Set(snap.expenses.map((e) => e.id));

              // A table edited locally more recently than the server wins.
              const tables = { ...snap.openTables };
              for (const [id, local] of Object.entries(s.tables)) {
                const remote = tables[id];
                if (!remote || local.updatedAt > remote.updatedAt) tables[id] = local;
              }

              return {
                orders: merged,
                tables,
                offers: snap.offers,
                customTables: Array.from(
                  new Set([...snap.customTables, ...s.customTables]),
                ),
                // null means the menu tables are absent or empty: keep ours.
                categories: snap.categories
                  ? sortCategories(snap.categories)
                  : s.categories,
                menuItems: snap.menuItems ? sortItems(snap.menuItems) : s.menuItems,
                // Same rule for the floor plan and the settings — an empty
                // table means the migration has not run, not "delete everything".
                restaurantTables: snap.restaurantTables
                  ? sortTables(snap.restaurantTables)
                  : s.restaurantTables,
                settings: snap.settings ?? s.settings,
                restaurants: snap.restaurants.length ? snap.restaurants : s.restaurants,
                staff: snap.staff,
                expenses: isOwner
                  ? sortExpenses([
                      ...snap.expenses,
                      ...unsentExpenses.filter((e) => !remoteExpenseIds.has(e.id)),
                    ])
                  : [],
                // Empty means the migration has not run, not "delete the
                // heads" — the same rule the menu and floor plan follow.
                expenseCategories: isOwner
                  ? (snap.expenseCategories.length
                      ? sortExpenseCategories(snap.expenseCategories)
                      : s.expenseCategories)
                  : [],
              };
            });
          }

          set((s) => ({
            sync: {
              state: 'synced',
              pending: s.outbox.length,
              lastSyncedAt: Date.now(),
              lastError: null,
            },
          }));
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          set((s) => ({
            sync: {
              state: 'error',
              pending: s.outbox.length,
              lastSyncedAt: s.sync.lastSyncedAt,
              lastError: msg,
            },
          }));
        }
      },

      /**
       * Resolves which restaurants this account belongs to and picks one.
       *
       * A previously chosen restaurant is kept if the user is still a member of
       * it; otherwise the first one is opened. No membership leaves
       * `restaurantId` null, which the UI shows as "no access" rather than as
       * an empty till.
       */
      loadMembership: async () => {
        const { restaurants, roles, isPlatformAdmin } = await fetchMembership();
        // An administrator holds no membership anywhere, so filtering by role
        // would hand them an empty Admin screen — the one screen whose whole
        // job is the list of every restaurant.
        const mine = isPlatformAdmin
          ? restaurants.filter((r) => r.active)
          : restaurants.filter((r) => roles[r.id] && r.active);
        const previous = get().restaurantId;
        const chosen = mine.find((r) => r.id === previous)?.id ?? mine[0]?.id ?? null;
        const role = chosen ? (roles[chosen] ?? null) : null;
        setActiveRestaurant(chosen);
        set((s) => ({
          restaurants: mine,
          restaurantId: chosen,
          role,
          isPlatformAdmin,
          // This runs on every sync, which is what makes a demotion actually
          // take effect: somebody dropped to worker in Admin loses the cached
          // costs here, on the next sync, without waiting for a pull to come
          // back empty or for the app to be restarted.
          expenses: role === 'owner' ? s.expenses : [],
          expenseCategories: role === 'owner' ? s.expenseCategories : [],
        }));
      },

      signIn: async (email, password) => {
        const sb = getSupabase();
        if (!sb) return 'No backend configured';
        const { data, error } = await sb.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (error) return error.message;

        // The shop, the role and the roster belong to whoever was signed in
        // before. On a shared counter device that is a different person, and
        // keeping their scope would show this one the wrong restaurant — and,
        // until the first sync landed, the wrong tabs. The till's own data is
        // left alone: an unsent order is not this person's to discard.
        const signedIn = data.user?.email ?? email.trim();
        const previous = get().userEmail;
        if (previous && previous.toLowerCase() !== signedIn.toLowerCase()) {
          setActiveRestaurant(null);
          set({
            restaurantId: null,
            role: null,
            isPlatformAdmin: false,
            restaurants: [],
            staff: [],
          });
        }

        set({ userEmail: signedIn });
        void get().syncNow();
        return null;
      },

      /**
       * Replaces this person's password with one they choose.
       *
       * Supabase will change a password on the strength of the session alone,
       * which is not enough here. These devices sit on a counter and stay
       * signed in through a shift, so "holding the session" and "being the
       * account holder" are different things. The old password is checked
       * first, by signing in with it, and only then is the new one set.
       *
       * That sign-in returns a fresh session for the same person, so nothing
       * is disturbed by it. A wrong password fails the sign-in and leaves the
       * existing session untouched — the caller is told, and nothing changes.
       *
       * No email is sent at any point, which is the whole reason this exists
       * rather than a reset link.
       */
      changePassword: async (current, next) => {
        const sb = getSupabase();
        if (!sb) return 'No backend configured';

        const email = get().userEmail;
        if (!email) return 'Sign in first';

        const { error: wrong } = await sb.auth.signInWithPassword({
          email,
          password: current,
        });
        // Deliberately not Supabase's own wording. "Invalid login credentials"
        // reads as "you are signed out" to somebody who plainly is not.
        if (wrong) return 'CURRENT_PASSWORD_WRONG';

        const { error } = await sb.auth.updateUser({ password: next });
        if (error) return error.message;
        return null;
      },

      signOut: async () => {
        const sb = getSupabase();
        await sb?.auth.signOut();
        setActiveRestaurant(null);
        // The roster and the restaurant list are the previous user's; the till
        // data stays so an unsent order is not lost when someone signs out.
        set((s) => ({
          userEmail: null,
          restaurantId: null,
          role: null,
          restaurants: [],
          staff: [],
          isPlatformAdmin: false,
          // Unlike the till data above, the costs do NOT stay. An unsent order
          // belongs to the shop and would be lost; the rent and the payroll
          // belong to the person signing out, and the next person to pick up
          // this tablet has no business with them. Anything still queued is in
          // the outbox and uploads regardless.
          expenses: [],
          expenseCategories: sortExpenseCategories(FALLBACK_EXPENSE_CATEGORIES),
          sync: { ...s.sync, state: 'signed_out' },
        }));
      },

      refreshSession: async () => {
        const sb = getSupabase();
        if (!sb) {
          set((s) => ({ sync: { ...s.sync, state: 'offline' } }));
          return;
        }
        const { data } = await sb.auth.getSession();
        const email = data.session?.user?.email ?? null;
        set({ userEmail: email });
        if (email) void get().syncNow();
        else set((s) => ({ sync: { ...s.sync, state: 'signed_out' } }));
      },
    }),
    {
      name: 'luck-family-app',
      version: 1,
      storage: createJSONStorage(() => deviceStorage),
      // Only durable data. View state, toasts and transient sync status are
      // deliberately left out so a restart lands on a clean screen.
      partialize: (s) => ({
        lang: s.lang,
        theme: s.theme,
        textSize: s.textSize,
        categories: s.categories,
        menuItems: s.menuItems,
        restaurantTables: s.restaurantTables,
        settings: s.settings,
        offers: s.offers,
        // Persisted so an owner can record a cash payment with no signal and
        // read last month's figures on the way to the bank. Cleared on sign-out
        // and on any sync that says this account is not an owner, so it is
        // never left behind for somebody else.
        expenses: s.expenses,
        expenseCategories: s.expenseCategories,
        tables: s.tables,
        customTables: s.customTables,
        orders: s.orders,
        outbox: s.outbox,
        deviceId: s.deviceId,
        userEmail: s.userEmail,
        restaurantId: s.restaurantId,
        role: s.role,
        // Persisted alongside `role` on purpose: if one survives a restart and
        // the other does not, an administrator reopening the app offline is
        // shown a till they have no membership for. Both are refreshed on the
        // next sync regardless.
        isPlatformAdmin: s.isPlatformAdmin,
        restaurants: s.restaurants,
      }),
      onRehydrateStorage: () => (state) => {
        // The engine's scope lives in module state, which a restart clears.
        // Restoring it here means a queued write can flush before the network
        // is back and membership can be re-fetched.
        if (state?.restaurantId) setActiveRestaurant(state.restaurantId);
        state?.markHydrated?.();
      },
    },
  ),
);

// ---- selectors ----

/**
 * Whether this device is signed in as an owner of the restaurant it is working
 * in. What the Expenses tab is gated on — though the gate that matters is the
 * RLS policy, not this.
 */
export function selectIsOwner(s: AppState): boolean {
  return s.role === 'owner';
}

/** The order in progress at the selected table, if a table is selected. */
export function selectActiveTable(s: AppState): OpenTable | null {
  return s.activeTable ? (s.tables[s.activeTable] ?? null) : null;
}

/**
 * Shared empty array. Selectors must never build a fresh reference per call:
 * zustand v5 feeds them straight to useSyncExternalStore, which then sees a
 * changed snapshot on every render and loops until React gives up.
 */
const NO_LINES: CartLine[] = [];

export function selectActiveCart(s: AppState): CartLine[] {
  return selectActiveTable(s)?.cart ?? NO_LINES;
}

/**
 * Every table id that should appear on the picker, in order.
 *
 * NOT safe to pass to `useAppStore` directly — it allocates. Subscribe to
 * `restaurantTables` and call this inside a `useMemo`.
 *
 * `legacyCustom` are ids added on the build that predates the floor plan. They
 * are appended only if the plan does not already describe them, so a device
 * that upgrades mid-service does not lose a table it is currently serving.
 */
export function buildTableIds(
  restaurantTables: RestaurantTable[],
  legacyCustom: string[] = [],
): string[] {
  const active = sortTables(restaurantTables.filter((r) => r.active));
  const known = new Set(active.map((r) => r.id));
  return [...active.map((r) => r.id), ...legacyCustom.filter((id) => !known.has(id))];
}

/** The table record for an id, if the floor plan still describes it. */
export function findTable(
  restaurantTables: RestaurantTable[],
  tableId: string,
): RestaurantTable | undefined {
  return restaurantTables.find((r) => r.id === tableId);
}
